// The descriptions an issue replaced (Poulpe-439), as today's Revisions
// section shows them: collapsed, newest first, each with who replaced it and
// when, and its text read when it is opened. The server keeps one per author
// per ten minutes. The changes of the issue's fields are in its Record.
import { useEffect, useState } from 'react'
import type { Issue, IssueRevisionLine } from '../api/types'
import { Time } from '../components/Time'
import { Markdown } from '../markdown/Markdown'
import { useStore } from '../store/context'
import { actorName } from '../vocab'

export function IssueRevisions({ issue }: { issue: Issue }) {
  const store = useStore()
  const [lines, setLines] = useState<readonly IssueRevisionLine[]>([])
  // Read again whenever the issue changes: a new edit may have kept one.
  useEffect(() => {
    let live = true
    store.issueRevisions(issue.id).then(
      (read) => {
        if (live) setLines(read.revisions)
      },
      () => {
        // The history is not what the page needs to work; it keeps what it has.
      },
    )
    return () => {
      live = false
    }
  }, [store, issue.id, issue.updated_at])
  if (lines.length === 0) return null
  return (
    <details className="section record issue-revisions">
      <summary className="section-header">
        <h2>
          Earlier descriptions<span className="section-count">{lines.length}</span>
        </h2>
      </summary>
      <ol className="record-list">
        {lines.map((line) => (
          <Revision key={line.number} issue={issue.id} line={line} />
        ))}
      </ol>
    </details>
  )
}

function Revision({ issue, line }: { issue: string; line: IssueRevisionLine }) {
  const store = useStore()
  const [body, setBody] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const open = (opened: boolean) => {
    if (!opened || body !== null) return
    setFailed(false)
    store.issueRevision(issue, line.number).then((read) => setBody(read.body), () => setFailed(true))
  }
  return (
    <li className="record-entry">
      <details onToggle={(event) => open(event.currentTarget.open)}>
        <summary className="record-head">
          <span className={line.reason === 'conflict' ? 'tag tag-warn' : 'tag'}>{line.reason === 'conflict' ? 'Conflict' : 'Earlier text'}</span>
          <span className="record-author">{actorName(line.author)}</span>
          <Time iso={line.at} className="record-time" />
        </summary>
        {failed ? <p className="muted">That text could not be read.</p> : body === null ? <p className="muted">Reading…</p> : <Markdown source={body} />}
      </details>
    </li>
  )
}
